/**
 * 設定項目の説明文定義
 * SettingsModalとSOAP設定ページで共有するSingle Source of Truth
 */

export interface SettingDescription {
  title: string
  description: string
}

export interface SettingSection {
  title: string
  items: {
    [key: string]: SettingDescription
  }
}

export const settingDescriptions: {
  legacyCompat: SettingSection
  speechRecognition: SettingSection
  soapOutput: SettingSection
  rules: SettingSection
  soapDefaultSelection: SettingSection
  otherSettings: SettingSection
  displaySettings: SettingSection
} = {
  legacyCompat: {
    title: '旧版互換設定',
    items: {
      // 語彙 (log/207 §10): 「しっかり」=「時間をかけて品質を取る」の統一語。
      // 目的語で区別する — しっかり文字起こし = 転写経路 (transcription_mode) /
      // しっかりSOAP生成 = 生成の思考量 (is_quick_mode の反転)。
      // 旧ラベル「高速SOAP生成」は実体 (転写経路) と合わず、品質の良い側 (batch) を
      // 機能の欠如として提示していたため改称した。
      transcription_mode: {
        title: 'しっかり文字起こし',
        description:
          '録音を止めたあとに録音全体をまとめて文字起こしします（正確さ優先・SOAP生成に時間がかかります）。オフの場合は録音中にリアルタイムで文字起こしします。',
      },
      soap_prompt_version: {
        title: 'SOAP生成モデル',
        description:
          'SOAP生成に使用するモデルの系統を選択します。次の録音から反映され、元の系統にいつでも戻せます。',
      },
      use_legacy_ui: {
        title: '従来の画面',
        description:
          'オンにすると従来のクラシック画面に切り替わります。再読み込み後に反映されます。',
      },
    },
  },
  speechRecognition: {
    title: '音声認識設定',
    items: {
      is_quick_mode: {
        // 旧「しっかりモード」。「しっかり文字起こし」(転写経路) と目的語で区別する (log/207 §10)。
        title: 'しっかりSOAP生成',
        description:
          'より深くSOAP執筆を検討するようにします（処理時間が伸びる可能性があります）。',
      },
      // 文字起こし時 = 文字起こしプロンプトへの指示 (back 側の処理)。
      // 録音時の信号処理 (capture_noise_suppression) とは別軸なので目的語で区別する。
      is_noise_cancelling: {
        title: '文字起こし時のノイズ抑制',
        description:
          '隣の投薬や店内アナウンスを軽減し、文字起こし性能を向上します。',
      },
      // 録音時のブラウザ側ノイズ抑制 (getUserMedia の noiseSuppression)。
      capture_noise_suppression: {
        title: '録音時のノイズ抑制',
        description:
          '録音中に周囲の雑音を抑えます。オンにすると小さな声まで雑音として抑えられ、聞き取れなくなることがあります。患者さんの声が小さく文字起こしに残らない場合はオフにしてください。',
      },
    },
  },
  soapOutput: {
    title: 'SOAP出力設定',
    items: {
      is_enrich_subjective: {
        title: 'S項目の内容を幅広に(β版)',
        description:
          'S情報をより幅広にSOAPに含めます。※ SOAPバージョン v1.x 選択時のみ有効です。',
      },
      should_enhance_soap: {
        title: 'A・OP・CPの拡充',
        description: '薬剤師の皆様が考えたであろうA・OP・CPを補足します。',
      },
      is_short_ep: {
        title: 'EPを要約',
        description:
          '投薬会話が長くなりEPも多い場合に、EPの項目数を圧縮し、SOAP視認性を向上します。',
      },
      use_soap_template: {
        title: 'SOAPテンプレート',
        description: '登録したSOAPテンプレートをSOAP生成時に展開します。',
      },
    },
  },
  // 旧マスタ管理グループ廃止に伴う押し込み先 (SOAP生成設定 > ルール)。
  // v3 は soap_rules のみ、v1/v2 は旧3画面リンクを表示する
  rules: {
    title: 'ルール・テンプレート',
    items: {
      soap_rules: {
        title: 'SOAPルール',
        description:
          '会話キーワードでの追記や、文字列の置換ルールを管理します。',
      },
      tracing_reports: {
        title: 'トレーシングレポート',
        description:
          '対象の医薬品グループが新規処方されたら自動作成し、提出の旨をSOAPに記載します。',
      },
      soap_templates: {
        title: '医薬品グループ',
        description:
          'グループ単位の医薬品リストと、SOAPテンプレート・キーワードを管理します。',
      },
      format_insert: {
        title: 'フォーマット自動挿入設定',
        description:
          'キーワード検出時に決まった文言を自動で挿入するルールを管理します。',
      },
      text_replacements: {
        title: '文字列置換設定',
        description: 'SOAP生成後に適用する文字列置換ルールを管理します。',
      },
    },
  },
  soapDefaultSelection: {
    title: 'SOAPデフォルト選択',
    items: {
      is_default_selected_subjectives: {
        title: 'S（主観的情報）',
        description: 'SOAP生成時にS項目をデフォルトで選択状態にします。',
      },
      is_default_selected_objectives: {
        title: 'O（客観的情報）',
        description:
          'SOAP生成時にO項目をデフォルトで選択状態にします。薬歴側の機能でO項目を補完する場合、オフにすることで視認性が上がります。',
      },
      is_default_selected_assessments: {
        title: 'A（評価）',
        description: 'SOAP生成時にA項目をデフォルトで選択状態にします。',
      },
      is_default_selected_educational_plans: {
        title: 'EP（教育計画）',
        description:
          'SOAP生成時にEP項目をデフォルトで選択状態にします。定型文を使う場合、オフにすると便利です。',
      },
      is_default_selected_care_plans: {
        title: 'CP（ケア計画）',
        description: 'SOAP生成時にCP項目をデフォルトで選択状態にします。',
      },
      is_default_selected_observational_plans: {
        title: 'OP（観察計画）',
        description: 'SOAP生成時にOP項目をデフォルトで選択状態にします。',
      },
    },
  },
  otherSettings: {
    title: 'その他設定',
    items: {},
  },
  displaySettings: {
    title: '表示設定',
    items: {
      display_days: {
        title: '薬歴表示日数',
        description:
          '薬歴リストに表示する日数（1〜7日）。少ないほど表示が速くなります。',
      },
    },
  },
}
